import { Button, HStack, Icon } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { ChevronRight } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import {
  CourseCoverageBadgeDetailed,
  type ResourceCoverageLike,
} from '../../components/translations/TranslationCoverageBadge';
import {
  courseHasPendingTranslation,
  TRANSLATION_POLL_INTERVAL_MS,
} from '../../components/translations/coverageStatus';
import { testIds } from './testIds';

// The course-level translation rollup, sitting in the Units card header with the
// content it describes. One chip and a link; the full per-language view lives on
// the dedicated coverage page. Shares the units list's listByCourse cache entry.
export function UnitsCoverageSummary({ courseId }: { courseId: string }) {
  const { data } = trpcAdmin.contentTranslations.listByCourse.useQuery(
    { courseId },
    {
      refetchInterval: (query) =>
        courseHasPendingTranslation(query.state.data?.units) ? TRANSLATION_POLL_INTERVAL_MS : false,
    },
  );

  if (data == null || data.supportedLanguages.length === 0) {
    return null;
  }

  const resources: ResourceCoverageLike[] = data.units.flatMap((unit) =>
    unit.resources.map((resource) => ({
      currentPublishedVersionId: resource.currentPublishedVersionId,
      rows: resource.rows,
    })),
  );

  return (
    <HStack spacing={1}>
      <CourseCoverageBadgeDetailed
        resources={resources}
        languages={data.supportedLanguages}
        data-testid={testIds.coverageBadge}
      />
      <Button
        as={RouterLink}
        to={`/courses/${courseId}/translations`}
        size="sm"
        variant="ghost"
        rightIcon={<Icon as={ChevronRight} boxSize={iconSize.sm} />}
        data-testid={testIds.viewCoverageLink}
      >
        <Trans>View coverage</Trans>
      </Button>
    </HStack>
  );
}
